import React, { Fragment, useEffect } from "react";
import { useTranslation } from "react-i18next";
import { Link } from "react-router-dom";
import SEO from "../../components/SEO/SEO";
import Header from "../../components/Header/Header";
import PageTitle from "../../components/pagetitle/PageTitle";
import ModulesGrid from "../../components/ModulesGrid/ModulesGrid";
import Footer from "../../components/footer/Footer";
import Scrollbar from "../../components/scrollbar/scrollbar";

import serviceBg from "../../images/bg/pricing-bg.png";
import cardBg from "../../images/bg/pricing-bg01.png";
import subLeftIcon from "../../images/icon/sub-left-icon.png";
import subRightIcon from "../../images/icon/sub-right-icon.png";

const FeaturesPage: React.FC = () => {
  const { t } = useTranslation();

  return (
    <Fragment>
      <SEO title="Features" description="Explore AXOIX features — 22 AI characters, 6,100+ voice commands in 81 languages, AI Costing Engine, LINK phone system, SEAL e-signatures, CHORUS social media, and more." path="/features" />
      <div className="about-page inner-page">
        <div className="body_wrap o-clip">
          <Header />
          <main
            style={{
              backgroundImage: `url(${serviceBg})`,
              backgroundSize: "cover",
              backgroundPosition: "center top",
              backgroundRepeat: "no-repeat",
              backgroundAttachment: "fixed",
            }}
          >
            <PageTitle pageTitle={t('services.pageTitle')} pagesub={t('services.pageSub')} />

            {/* Intro */}
            <section className="pt-60 pb-0">
              <div className="container text-center">
                <p className="opacity-75" style={{ fontSize: "1.1rem", maxWidth: 750, margin: "0 auto" }}>
                  22 business modules, each powered by a dedicated AI character.
                  Click any module to explore its full capabilities.
                </p>
              </div>
            </section>

            {/* Modules Grid */}
            <section className="pt-60 pb-80">
              <div className="container">
                <div className="sec-title sec-title--two text-center mb-50">
                  <span className="sub-title mb-20">
                    <img src={subLeftIcon} alt="" /> Powerful Modules{" "}
                    <img src={subRightIcon} alt="" />
                  </span>
                  <h2 className="title">{t('services.introTitle')}</h2>
                </div>
                <ModulesGrid />
              </div>
            </section>

            {/* API Integrations */}
            <section className="pb-80">
              <div className="container">
                <div className="pricing-item xb-border bg_img p-40" style={{ backgroundImage: `url(${cardBg})`, borderRadius: 20 }}>
                  <div className="row align-items-center">
                    <div className="col-lg-6">
                      <span className="sub-title mb-15 d-block">Pre-Built Integrations</span>
                      <h3 className="mb-20">150+ API Integrations Ready to Use</h3>
                      <p className="opacity-75 mb-20">
                        AXOIX comes with pre-integrated APIs for Google (Gmail, Calendar, Drive, Docs, Sheets, Google Ads, Analytics 4, YouTube, Business Profile and Classroom), payments (UPI, Razorpay, Stripe), messaging (MSG91, Twilio, WhatsApp), travel (Amadeus GDS), accounting (Tally, QuickBooks), weather, news, currency, flights, railways, maps, stocks, AI/ML, and government data (Aadhaar, PAN, GST verification). 150+ BYOK providers ready to use.
                      </p>
                      <Link to="/feature/google-workspace" className="thm-btn agency-btn" style={{ display: "inline-block" }}>
                        <span className="text">Explore the Google modules</span>
                      </Link>
                    </div>
                    <div className="col-lg-6">
                      <div className="d-flex flex-wrap gap-3 justify-content-center">
                        {["Gmail", "Google Calendar", "Google Drive", "Google Ads", "Analytics 4", "YouTube", "Google Classroom", "UPI Payments", "Razorpay", "Stripe", "WhatsApp CRM", "Shopify", "Shiprocket", "IndiaMART", "Amadeus GDS", "Tally", "Govt Data"].map((api, idx) => (
                          <span key={idx} className="xb-border px-3 py-2" style={{ borderRadius: 10, fontSize: 14 }}>
                            {api}
                          </span>
                        ))}
                      </div>
                    </div>
                  </div>
                </div>
              </div>
            </section>

            {/* WhatsApp CRM Inbox */}
            <section className="pb-80">
              <div className="container">
                <div className="pricing-item xb-border bg_img p-40" style={{ backgroundImage: `url(${cardBg})`, borderRadius: 20 }}>
                  <div className="row align-items-center">
                    <div className="col-lg-5 mb-30 mb-lg-0">
                      <span className="sub-title mb-15 d-block">Multi-Channel Communication</span>
                      <h3 className="mb-20">WhatsApp CRM Inbox</h3>
                      <p className="opacity-75 mb-20">
                        Unified shared inbox for WhatsApp, Facebook Messenger, Instagram DM, Telegram, SMS, and web chat. Every conversation gets SLA tracking with 5-level escalation, AI auto-replies, and drip sequences for automated follow-ups.
                      </p>
                    </div>
                    <div className="col-lg-7">
                      <div className="row">
                        {[
                          { icon: "fab fa-whatsapp", title: "WhatsApp Business", desc: "Send & receive WhatsApp messages directly from your dashboard" },
                          { icon: "fas fa-robot", title: "AI Auto-Replies", desc: "Intent detection, sentiment analysis, and confidence-based auto-responses" },
                          { icon: "fas fa-tachometer-alt", title: "SLA Tracking", desc: "5-level escalation: Agent → Team Lead → Manager → Director → C-Level" },
                          { icon: "fas fa-paper-plane", title: "Drip Sequences", desc: "Multi-step timed campaigns: welcome → catalog → offer with delays" },
                          { icon: "fas fa-users", title: "Agent Assignment", desc: "Round-robin auto-assign with workload balancing and internal notes" },
                          { icon: "fas fa-chart-bar", title: "Performance Analytics", desc: "Resolution rate, avg response time, SLA breaches per agent" },
                        ].map((item, idx) => (
                          <div key={idx} className="col-md-6 mb-20">
                            <div className="d-flex align-items-start gap-3">
                              <div className="xb-border d-flex align-items-center justify-content-center" style={{ width: 45, height: 45, borderRadius: 10, fontSize: 18, flexShrink: 0 }}>
                                <i className={item.icon} style={{ color: "var(--color-primary)" }}></i>
                              </div>
                              <div>
                                <h6 className="mb-1" style={{ fontSize: 14 }}>{item.title}</h6>
                                <p className="mb-0 opacity-50" style={{ fontSize: 12 }}>{item.desc}</p>
                              </div>
                            </div>
                          </div>
                        ))}
                      </div>
                    </div>
                  </div>
                </div>
              </div>
            </section>

            {/* E-Commerce Connectors */}
            <section className="pb-80">
              <div className="container">
                <div className="pricing-item xb-border bg_img p-40" style={{ backgroundImage: `url(${cardBg})`, borderRadius: 20 }}>
                  <div className="row align-items-center">
                    <div className="col-lg-5 mb-30 mb-lg-0">
                      <span className="sub-title mb-15 d-block">Sell Everywhere</span>
                      <h3 className="mb-20">Shopify, Shiprocket & IndiaMART</h3>
                      <p className="opacity-75 mb-20">
                        Connect your Shopify stores for real-time product, order, and customer sync. Ship with 25+ courier partners via Shiprocket. Auto-capture IndiaMART leads into your CRM. Cart recovery and order notifications via WhatsApp included.
                      </p>
                    </div>
                    <div className="col-lg-7">
                      <div className="row">
                        {[
                          { icon: "fab fa-shopify", title: "Shopify Sync", desc: "Products, orders, customers — real-time bidirectional sync" },
                          { icon: "fas fa-truck", title: "Shiprocket Shipping", desc: "25+ couriers, AWB generation, live tracking, COD support" },
                          { icon: "fas fa-bullhorn", title: "IndiaMART Leads", desc: "Auto-capture inquiries into CRM with dedup & auto-assign" },
                          { icon: "fas fa-shopping-cart", title: "Cart Recovery", desc: "Abandoned carts auto-trigger WhatsApp/email recovery messages" },
                          { icon: "fas fa-bell", title: "Order Notifications", desc: "WhatsApp/SMS/email on: confirmed, shipped, delivered, refunded" },
                          { icon: "fas fa-store", title: "Dual-Channel Selling", desc: "Products on Shopify AND your AXOIX Storefront simultaneously" },
                        ].map((item, idx) => (
                          <div key={idx} className="col-md-6 mb-20">
                            <div className="d-flex align-items-start gap-3">
                              <div className="xb-border d-flex align-items-center justify-content-center" style={{ width: 45, height: 45, borderRadius: 10, fontSize: 18, flexShrink: 0 }}>
                                <i className={item.icon} style={{ color: "var(--color-primary)" }}></i>
                              </div>
                              <div>
                                <h6 className="mb-1" style={{ fontSize: 14 }}>{item.title}</h6>
                                <p className="mb-0 opacity-50" style={{ fontSize: 12 }}>{item.desc}</p>
                              </div>
                            </div>
                          </div>
                        ))}
                      </div>
                    </div>
                  </div>
                </div>
              </div>
            </section>

            {/* CHORUS Studio 2.0 */}
            <section className="pb-80">
              <div className="container">
                <div className="pricing-item xb-border bg_img p-40" style={{ backgroundImage: `url(${cardBg})`, borderRadius: 20 }}>
                  <div className="row align-items-center">
                    <div className="col-lg-5 mb-30 mb-lg-0">
                      <span className="sub-title mb-15 d-block">AI Content Pipeline</span>
                      <h3 className="mb-20">CHORUS Studio 2.0</h3>
                      <p className="opacity-75 mb-20">
                        Generate blog articles, social posts, podcasts, and videos with a multi-agent AI pipeline. Voice Studio with 5 TTS providers including voice cloning. Video Studio with AI generation. Content Calendar with distribution to 14 platforms.
                      </p>
                    </div>
                    <div className="col-lg-7">
                      <div className="row">
                        {[
                          { icon: "fas fa-pen-fancy", title: "AI Content Writer", desc: "Blog articles, social posts, emails — 13 content types" },
                          { icon: "fas fa-microphone-alt", title: "Podcast Studio", desc: "Shows, episodes, segments, RSS feed — fully automated" },
                          { icon: "fas fa-video", title: "Video Studio", desc: "AI video generation in 16:9, 9:16, and 1:1 aspect ratios" },
                          { icon: "fas fa-volume-up", title: "Voice Studio", desc: "ElevenLabs, OpenAI TTS, Google TTS, Murf, Bark + voice cloning" },
                          { icon: "fas fa-palette", title: "Brand Engine", desc: "Voice profiles and training samples for consistent brand tone" },
                          { icon: "fas fa-calendar-alt", title: "Content Calendar", desc: "Schedule and distribute to 14 platforms from one dashboard" },
                        ].map((item, idx) => (
                          <div key={idx} className="col-md-6 mb-20">
                            <div className="d-flex align-items-start gap-3">
                              <div className="xb-border d-flex align-items-center justify-content-center" style={{ width: 45, height: 45, borderRadius: 10, fontSize: 18, flexShrink: 0 }}>
                                <i className={item.icon} style={{ color: "var(--color-primary)" }}></i>
                              </div>
                              <div>
                                <h6 className="mb-1" style={{ fontSize: 14 }}>{item.title}</h6>
                                <p className="mb-0 opacity-50" style={{ fontSize: 12 }}>{item.desc}</p>
                              </div>
                            </div>
                          </div>
                        ))}
                      </div>
                    </div>
                  </div>
                </div>
              </div>
            </section>

            {/* Security */}
            <section className="pb-80">
              <div className="container">
                <div className="pricing-item xb-border bg_img p-40" style={{ backgroundImage: `url(${cardBg})`, borderRadius: 20 }}>
                  <div className="row align-items-center">
                    <div className="col-lg-5 mb-30 mb-lg-0">
                      <span className="sub-title mb-15 d-block">5 Layers of Defense</span>
                      <h3 className="mb-20">Fortress-Grade Infrastructure</h3>
                      <p className="opacity-75 mb-20">
                        Your dashboard is protected by 5 layers of active defense — from network firewall to application-layer encryption. SSH is key-only, HSTS is preloaded in all major browsers, and automatic security patching keeps everything current.
                      </p>
                      <div className="d-flex align-items-center gap-3">
                        <i className="fas fa-shield-alt" style={{ fontSize: 40, color: "var(--color-primary)" }}></i>
                        <span style={{ fontSize: 14 }} className="opacity-75">
                          HSTS preloaded · Full CSP · SSH key-only · Auto-patching enabled
                        </span>
                      </div>
                    </div>
                    <div className="col-lg-7">
                      <div className="row">
                        {[
                          { icon: "fas fa-fire", title: "iptables Firewall", desc: "Default-deny policy — only explicitly allowed ports open" },
                          { icon: "fas fa-globe", title: "CrowdSec Intelligence", desc: "Collaborative blocklists shared across 100K+ servers" },
                          { icon: "fas fa-shield-alt", title: "ModSecurity WAF", desc: "1,432 OWASP rules blocking SQLi & XSS in real-time" },
                          { icon: "fas fa-ban", title: "Fail2ban (8 Jails)", desc: "Auto-bans brute-force attackers across SSH, HTTP & more" },
                          { icon: "fas fa-tachometer-alt", title: "Nginx Rate Limiting", desc: "Request throttling prevents abuse and DDoS attempts" },
                          { icon: "fas fa-database", title: "Data Encryption", desc: "MySQL TDE + encrypted binlogs + AES on PII fields" },
                        ].map((item, idx) => (
                          <div key={idx} className="col-md-6 mb-20">
                            <div className="d-flex align-items-start gap-3">
                              <div className="xb-border d-flex align-items-center justify-content-center" style={{ width: 45, height: 45, borderRadius: 10, fontSize: 18, flexShrink: 0 }}>
                                <i className={item.icon} style={{ color: "var(--color-primary)" }}></i>
                              </div>
                              <div>
                                <h6 className="mb-1" style={{ fontSize: 14 }}>{item.title}</h6>
                                <p className="mb-0 opacity-50" style={{ fontSize: 12 }}>{item.desc}</p>
                              </div>
                            </div>
                          </div>
                        ))}
                      </div>
                    </div>
                  </div>
                </div>
              </div>
            </section>

            {/* CTA */}
            <section className="pb-100">
              <div className="container text-center">
                <div className="pricing-item xb-border bg_img p-50" style={{ backgroundImage: `url(${cardBg})`, borderRadius: 20 }}>
                  <h2 className="mb-15">Ready to Transform Your Business?</h2>
                  <p className="opacity-75 mb-30" style={{ maxWidth: 600, margin: "0 auto 30px" }}>
                    Join thousands of businesses using AXOIX to automate operations, improve efficiency, and grow faster. Sign up for the free Explorer plan and experience the full suite.
                  </p>
                  <div className="d-flex gap-3 justify-content-center flex-wrap">
                    <Link to="/signup" className="thm-btn agency-btn">
                      <span className="text">Join Now</span>
                    </Link>
                    <Link to="/pricing" className="thm-btn agency-btn" style={{ background: "transparent", border: "1px solid rgba(255,255,255,0.2)" }}>
                      <span className="text">View Pricing</span>
                    </Link>
                  </div>
                </div>
              </div>
            </section>

          </main>
          <Footer />
          <Scrollbar />
        </div>
      </div>
    </Fragment>
  );
};

export default FeaturesPage;
